import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserBadge } from '@the-wall/shared';
import { api } from '@/services/api';
import { useChatStore } from '@/stores/chatStore';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  handleOAuthCallback: (token: string) => Promise<void>;
  register: (email: string, password: string, username: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateProfile: (updates: {
    profilePicture?: string | null;
    usernameColor?: string | null;
  }) => Promise<void>;
  updateInkBalance: (inkBalance: number) => void;
  updateSelectedBadges: (selectedBadges: UserBadge[]) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.login(email, password);
          set({ user: response.user, token: response.token, isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      loginWithGoogle: async () => {
        // Redirect to Google OAuth flow
        window.location.href = '/api/auth/google';
      },

      handleOAuthCallback: async (token) => {
        set({ isLoading: true, error: null, token });
        try {
          const user = await api.getCurrentUser(token);
          set({ user, isLoading: false });
        } catch (error) {
          set({
            user: null,
            token: null,
            error: error instanceof Error ? error.message : 'OAuth login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (email, password, username) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.register(email, password, username);
          set({ user: response.user, token: response.token, isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: () => {
        set({ user: null, token: null, error: null });
      },

      checkAuth: async () => {
        const { token } = get();
        if (!token) {
          set({ isLoading: false });
          return;
        }

        try {
          const user = await api.getCurrentUser(token);
          set({ user, isLoading: false });
        } catch {
          set({ user: null, token: null, isLoading: false });
        }
      },

      updateProfile: async (updates) => {
        const { token } = get();
        if (!token) {
          throw new Error('Not authenticated');
        }

        try {
          const user = await api.updateProfile(token, updates);
          set({ user });
          useChatStore.getState().updateUserAppearance(user.id, {
            usernameColor: user.usernameColor ?? null,
            usernameEffect: user.usernameEffect ?? null,
          });
        } catch (error) {
          throw error;
        }
      },

      updateInkBalance: (inkBalance) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, inkBalance } });
        }
      },

      updateSelectedBadges: (selectedBadges) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, selectedBadges } });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'the-wall-auth',
      partialize: (state) => ({ token: state.token }),
      onRehydrateStorage: () => (state) => {
        state?.checkAuth();
      },
    }
  )
);
